import {
  getMarkdownPreview,
  markdownToPlainText,
} from '@littlebird/markdown-utils';
import {
  type GeneratedRoutineSuggestion,
  ROUTINE_TRIGGER_GMAIL,
  ROUTINE_TRIGGER_MEETING_STARTED,
  ROUTINE_TRIGGER_MEETING_SUMMARIZED,
  ROUTINE_TRIGGER_OUTLOOK,
  ROUTINE_TRIGGER_WEBHOOK,
  type RoutineLibraryItem,
  type RoutineLibraryTab,
  type RoutineTriggerDraft,
  useStores,
} from '@littlebird/sdk';
import {
  dedupeRoutineSuggestions,
  formatRoutineWeekDays,
  normalizeRoutineTitle,
  suggestionToRoutineTemplate,
} from '@littlebird/ui-utils';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { ModalsManager } from '@/components/modals/modalsManager';
import { Button, Text } from '@/components/ui';
import { EntityIconChip } from '@/components/ui/icon-picker';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { ScrollMask } from '@/components/ui/ScrollMask';
import { ScrollRestoration } from '@/components/ui/ScrollRestoration';
import { Skeleton } from '@/components/ui/skeleton';
import { Markdown } from '@/components/ui/smoothdown';
import { Tooltip } from '@/components/ui/Tooltip';
import { MEETING_TRIGGER_COPY } from '@/features/routines/routine-meeting-triggers';
import { LibraryCardMenu } from '@/features/routines/routine-team-share';
import { CreateRoutineModal } from '@/features/routines/routines-create';
import {
  useRoutineLibrary,
  useRoutineSuggestions,
} from '@/features/routines/use-routine-refresh';
import { useRoutinesLimitState } from '@/features/subscription/hooks/use-routines-limit-state';
import {
  getIntegrationDisplayName,
  getIntegrationIcon,
} from '@/integrations/integration-icons';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';
import { formatTimeFromHHmm } from '@/utils/date';

import {
  templateSourceOf,
  useTemplateImpressions,
} from '../routine-template-analytics';

const openTemplateModal = (item: RoutineLibraryItem) =>
  ModalsManager.show(CreateRoutineModal, { libraryItem: item });

/** Opens the modal for a `?template=` id, then clears the param so the link stays re-clickable. */
export const useRoutineTemplateDeepLink = () => {
  const navigate = useNavigate();
  const { template } = useSearch({ strict: false }) as { template?: string };
  const { items, isLoading } = useRoutineLibrary();
  const handledTemplate = useRef<string | null>(null);

  useEffect(() => {
    if (!template) {
      handledTemplate.current = null;
      return;
    }
    // Wait for the library, otherwise a valid id looks unknown on a cold load
    if (isLoading || handledTemplate.current === template) return;
    handledTemplate.current = template;

    const item = items.find((libraryItem) => libraryItem.id === template);
    if (item) {
      openTemplateModal(item);
    } else {
      toast.error('That routine template is no longer available');
    }
    navigate({
      to: '.',
      search: (prev) => ({ ...prev, template: undefined }),
      replace: true,
    });
  }, [template, isLoading, items, navigate]);
};

const FEATURED_SUGGESTION_TITLES = [
  'Morning Brief',
  "Don't Let Me Drop the Ball",
  'Draft Responses to Important Emails',
] as const;

const ordinalSuffix = (n: number) =>
  ['th', 'st', 'nd', 'rd'][(n % 100) - 20] ||
  ['th', 'st', 'nd', 'rd'][n % 100] ||
  'th';

const formatTrigger = (trigger: RoutineTriggerDraft) => {
  switch (trigger.kind) {
    case ROUTINE_TRIGGER_GMAIL:
      return 'On new email';
    case ROUTINE_TRIGGER_OUTLOOK:
      return 'On new Outlook email';
    case ROUTINE_TRIGGER_WEBHOOK:
      return 'On webhook';
    case ROUTINE_TRIGGER_MEETING_STARTED:
    case ROUTINE_TRIGGER_MEETING_SUMMARIZED:
      return `When ${MEETING_TRIGGER_COPY[trigger.kind].title.toLowerCase()}`;
    default:
      return 'On a trigger';
  }
};

const formatSchedule = (item: RoutineLibraryItem) => {
  const time = formatTimeFromHHmm(item.trigger_time);
  if (item.frequency === 'Daily') return `Daily at ${time}`;
  if (item.frequency === 'Weekly' && item.week_day)
    return `Weekly on ${formatRoutineWeekDays([item.week_day])} at ${time}`;
  if (item.frequency === 'Monthly' && item.month_day)
    return `Monthly on the ${item.month_day}${ordinalSuffix(item.month_day)} at ${time}`;
  return 'Scheduled';
};

const MAX_VISIBLE_RUN_CONDITIONS = 2;

const formatRunConditions = (item: RoutineLibraryItem) => {
  const triggers = item.triggers ?? [];
  // A template with neither a rule nor a trigger still gets a schedule label
  const showsSchedule = Boolean(item.recurrence_rule) || !triggers.length;
  const conditions = [
    ...new Set([
      ...(showsSchedule ? [formatSchedule(item)] : []),
      ...triggers.map(formatTrigger),
    ]),
  ];
  const visibleConditions = conditions
    .slice(0, MAX_VISIBLE_RUN_CONDITIONS)
    .join(' · ');
  const hiddenCount = conditions.length - MAX_VISIBLE_RUN_CONDITIONS;
  return {
    label:
      hiddenCount > 0
        ? `${visibleConditions} +${hiddenCount} more`
        : visibleConditions,
    fullLabel: conditions.join(' · '),
  };
};

const IntegrationBadges = ({ slugs }: { slugs: string[] }) => {
  if (!slugs.length) return null;
  return (
    <XStack className="shrink-0 items-center gap-1.5">
      {slugs.map((slug) => {
        const Icon = getIntegrationIcon(slug);
        const name = getIntegrationDisplayName(slug);
        return (
          <Tooltip key={slug} label={name}>
            <span
              role="img"
              aria-label={name}
              className="inline-flex items-center justify-center size-6 rounded bg-surface-active text-foreground-muted"
            >
              <Icon className="size-3.5" />
            </span>
          </Tooltip>
        );
      })}
    </XStack>
  );
};

type LibraryTabsProps = {
  tabs: RoutineLibraryTab[];
  active: string;
  onChange: (slug: string) => void;
};

const LibraryTabs = ({ tabs, active, onChange }: LibraryTabsProps) => (
  <XStack className="items-center gap-5 overflow-x-auto overflow-y-hidden border-b border-border scrollbar-hide">
    {tabs.map((tab) => {
      const isActive = tab.slug === active;
      return (
        <button
          key={tab.slug}
          type="button"
          onClick={() => onChange(tab.slug)}
          className={cn(
            'inline-flex shrink-0 items-center gap-1.5 px-1 pb-2 -mb-px border-b-2 transition-colors focus-visible:border-foreground focus-visible:outline-none',
            isActive
              ? 'border-foreground text-foreground'
              : 'border-transparent text-foreground-muted hover:text-foreground'
          )}
        >
          <Text className={cn('text-sm', isActive && 'font-medium')}>
            {tab.label}
          </Text>
        </button>
      );
    })}
  </XStack>
);

type LibraryCardProps = {
  item: RoutineLibraryItem;
  onSelect: (item: RoutineLibraryItem) => void;
  disabled: boolean;
  expanded?: boolean;
};

export const LibraryCard = ({
  item,
  onSelect,
  disabled,
  expanded = false,
}: LibraryCardProps) => {
  const preview = useMemo(
    () => getMarkdownPreview(item.description || item.prompt),
    [item.description, item.prompt]
  );
  const runConditions = formatRunConditions(item);
  const card = (
    <button
      type="button"
      onClick={() => onSelect(item)}
      disabled={disabled}
      className={cn(
        'group flex h-full w-full flex-col rounded-2xl border border-border bg-surface text-left',
        'transition-all duration-200',
        disabled ? 'opacity-60 cursor-not-allowed' : 'hover:bg-surface-hover'
      )}
    >
      <YStack className="justify-between flex-1 px-6 py-5 gap-3">
        <YStack className="gap-1.5">
          <Text
            className={cn(
              'min-w-0 font-medium group-hover:text-foreground',
              !expanded && 'truncate pr-8'
            )}
          >
            {item.title}
          </Text>
          {expanded && item.tags.includes('suggested') && (
            <span
              title="Suggested starting point"
              className="w-fit rounded-full bg-green-500/10 px-2 py-0.5 text-xs text-green-700 dark:text-green-300"
            >
              For you
            </span>
          )}
          <div
            inert
            className={cn(
              'pointer-events-none text-sm leading-5 text-foreground-muted',
              !expanded && 'max-h-10 overflow-hidden'
            )}
          >
            <Markdown
              id={`routine-library-preview-${item.id}`}
              content={preview}
              className="prose-sm max-w-none text-inherit [&_*]:text-inherit [&>*]:m-0 [&_ol]:pl-4 [&_p]:leading-5 [&_ul]:pl-4"
            />
          </div>
        </YStack>
        <XStack
          wrap={expanded}
          className="min-h-6 items-center justify-between gap-3"
        >
          <span
            title={item.schedule_label ?? runConditions.fullLabel}
            className={cn(
              'min-w-0 leading-5 text-foreground-muted',
              expanded ? 'text-xs' : 'text-xs.5 truncate'
            )}
          >
            {item.schedule_label ?? runConditions.label}
          </span>
          {expanded ? (
            <XStack wrap className="items-center gap-1.5">
              <IntegrationBadges slugs={item.integrations.slice(0, 3)} />
              {item.integrations.length > 3 && (
                <span
                  title={item.integrations
                    .slice(3)
                    .map(getIntegrationDisplayName)
                    .join(', ')}
                  className="rounded-full border border-border px-2 text-xs text-foreground-muted"
                >
                  +{item.integrations.length - 3}
                </span>
              )}
            </XStack>
          ) : (
            <IntegrationBadges slugs={item.integrations} />
          )}
        </XStack>
      </YStack>
    </button>
  );

  return (
    <div className="relative h-full">
      {card}
      {!expanded && <LibraryCardMenu item={item} />}
    </div>
  );
};

type LibraryBrowserProps = {
  onSelect: (item: RoutineLibraryItem) => void;
  gridClassName?: string;
  heading?: string | null;
  scrollItems?: boolean;
};

export const LibraryBrowser = observer(function LibraryBrowser({
  onSelect,
  gridClassName = 'grid grid-cols-1 md:grid-cols-2 gap-4',
  heading = 'Browse the library',
  scrollItems = false,
}: LibraryBrowserProps) {
  const {
    items: libraryItems,
    tabs: libraryTabs,
    isLoading,
    isError,
  } = useRoutineLibrary();
  const { hasReachedLimit } = useRoutinesLimitState();
  const tabs = libraryTabs.filter(
    (tab) => tab.count > 0 && tab.slug !== 'tab:code'
  );

  const defaultTab = tabs.find((t) => t.count > 0)?.slug ?? tabs[0]?.slug ?? '';
  const [activeTab, setActiveTab] = useState<string>(defaultTab);
  const effectiveTab = activeTab || defaultTab;

  const filteredItems = useMemo(
    () =>
      effectiveTab
        ? libraryItems.filter((item) => item.tags.includes(effectiveTab))
        : libraryItems,
    [libraryItems, effectiveTab]
  );

  if (isLoading) {
    return (
      <YStack className="gap-4">
        {heading && <Skeleton className="h-6 w-1/2" />}
        <div className={gridClassName}>
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-40 rounded-2xl" />
          ))}
        </div>
      </YStack>
    );
  }

  if (isError || tabs.length === 0) return null;

  const items =
    filteredItems.length === 0 ? (
      <YStack className="items-center justify-center py-10">
        <Text className="text-sm text-foreground-muted">
          No templates in this category yet.
        </Text>
      </YStack>
    ) : (
      <div className={gridClassName}>
        {filteredItems.map((item) =>
          hasReachedLimit ? (
            <Tooltip key={item.id} label="You have reached your routine limit">
              <span className="block h-full">
                <LibraryCard item={item} onSelect={onSelect} disabled />
              </span>
            </Tooltip>
          ) : (
            <LibraryCard
              key={item.id}
              item={item}
              onSelect={onSelect}
              disabled={false}
            />
          )
        )}
      </div>
    );

  return (
    <YStack className={scrollItems ? 'h-full min-h-0' : 'gap-4'}>
      {heading && (
        <Text className="text-sm text-foreground-muted">{heading}</Text>
      )}
      <div className={cn(scrollItems && 'pr-3')}>
        <LibraryTabs
          tabs={tabs}
          active={effectiveTab}
          onChange={setActiveTab}
        />
      </div>
      {scrollItems ? (
        <ScrollMask
          key={effectiveTab}
          showEndFade
          className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto pb-6 pr-3 pt-4"
        >
          {items}
          <ScrollRestoration id={`routine-library-${effectiveTab}`} />
        </ScrollMask>
      ) : (
        items
      )}
    </YStack>
  );
});

export const RoutineLibrarySection = observer(function RoutineLibrarySection() {
  const { hasReachedLimit } = useRoutinesLimitState();

  const onSelect = (item: RoutineLibraryItem) => {
    if (hasReachedLimit) return;
    trackEvent('ROUTINE_LIBRARY_EVENT', {
      actionName: 'library_template_clicked',
      templateId: item.id,
      templateTitle: item.title,
      templateSource: templateSourceOf(item),
      surface: 'browse',
    });
    openTemplateModal(item);
  };

  return <LibraryBrowser onSelect={onSelect} />;
});

type CompactLibraryRowProps = {
  item: RoutineLibraryItem;
  disabled: boolean;
  onSelect: (item: RoutineLibraryItem) => void;
  sharedByYou?: boolean;
};

const CompactLibraryRow = ({
  item,
  disabled,
  onSelect,
  sharedByYou = false,
}: CompactLibraryRowProps) => {
  const ItemIcon = item.integrations[0]
    ? getIntegrationIcon(item.integrations[0])
    : Icons.routines;
  const sharedBy = sharedByYou ? 'You' : item.shared_by;
  const description = markdownToPlainText(
    getMarkdownPreview(item.description || item.prompt)
  );

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onSelect(item)}
      className="flex w-full min-w-0 items-center gap-4 rounded-2xl p-2 text-left transition-colors hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-50"
    >
      {item.icon ? (
        <EntityIconChip
          icon={item.icon}
          size={20}
          className="size-12 rounded-xl"
        />
      ) : (
        <span className="inline-flex size-12 shrink-0 items-center justify-center rounded-xl border-[0.5px] border-border-strong bg-surface text-foreground-muted">
          <ItemIcon className="size-5" strokeWidth={1.5} />
        </span>
      )}
      <YStack className="min-w-0 max-w-[46rem] flex-1 gap-0.5">
        <span className="truncate text-base font-medium text-foreground">
          {item.title}
        </span>
        {description && (
          <XStack className="min-w-0 items-center gap-1.5">
            {sharedBy && (
              <>
                <span className="shrink-0 text-xs font-medium text-foreground-muted">
                  {sharedBy}
                </span>
                <span
                  aria-hidden="true"
                  className="shrink-0 text-xs text-foreground-muted"
                >
                  ·
                </span>
              </>
            )}
            <span className="truncate text-xs text-foreground-muted">
              {description}
            </span>
          </XStack>
        )}
      </YStack>
    </button>
  );
};

const BrowseLibraryRow = ({ onSelect }: { onSelect: () => void }) => (
  <button
    type="button"
    onClick={onSelect}
    className="group flex w-full min-w-0 items-center gap-4 rounded-2xl p-2 text-left transition-colors hover:bg-surface-hover"
  >
    <span className="inline-flex size-12 shrink-0 items-center justify-center rounded-xl border-[0.5px] border-border-strong bg-surface text-foreground-muted">
      <Icons.book className="size-5" strokeWidth={1.5} />
    </span>
    <YStack className="min-w-0 flex-1 gap-0.5">
      <span className="text-base font-medium text-foreground">
        Browse library
      </span>
      <span className="text-xs text-foreground-muted">
        More routine templates
      </span>
    </YStack>
    <Icons.chevronRight className="mr-2 size-4 shrink-0 text-foreground-muted opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100" />
  </button>
);

type RoutineLibraryCompactSectionProps = {
  frequencies?: Array<NonNullable<RoutineLibraryItem['frequency']>>;
  query?: string;
  onAskLittlebird: () => void;
  onBrowseLibrary: () => void;
  onSelectTemplate: (item: RoutineLibraryItem) => void;
};

const useTeamLibrarySplit = () => {
  const { items, isLoading, isError } = useRoutineLibrary();
  const { routines } = useStores();

  const sharedByYouIds = new Set(
    routines.userRoutines.flatMap((routine) => routine.teamShare?.id ?? [])
  );
  const teamItems = items.filter((item) => item.source === 'team');

  return {
    isLoading,
    isError,
    teammateItems: teamItems.filter((item) => !sharedByYouIds.has(item.id)),
    sharedByYouItems: teamItems.filter((item) => sharedByYouIds.has(item.id)),
  };
};

export const RoutineTeamTemplatesSection = observer(
  function RoutineTeamTemplatesSection({
    onSelectTemplate,
  }: {
    onSelectTemplate: (item: RoutineLibraryItem) => void;
  }) {
    const { isLoading, isError, teammateItems, sharedByYouItems } =
      useTeamLibrarySplit();
    const { hasReachedLimit } = useRoutinesLimitState();

    useTemplateImpressions('team', [...teammateItems, ...sharedByYouItems]);

    const onSelect = (item: RoutineLibraryItem) => {
      if (hasReachedLimit) return;
      trackEvent('ROUTINE_LIBRARY_EVENT', {
        actionName: 'library_team_template_clicked',
        templateId: item.id,
        templateTitle: item.title,
        templateSource: 'team',
        surface: 'team',
      });
      onSelectTemplate(item);
    };

    if (isLoading || isError) return null;
    if (teammateItems.length === 0 && sharedByYouItems.length === 0)
      return null;

    return (
      <YStack className="gap-2">
        <Text className="px-2 text-sm text-foreground-muted">
          From your team
        </Text>
        <YStack className="gap-1">
          {teammateItems.map((item) => (
            <CompactLibraryRow
              key={item.id}
              item={item}
              disabled={hasReachedLimit}
              onSelect={onSelect}
            />
          ))}
          {sharedByYouItems.map((item) => (
            <CompactLibraryRow
              key={item.id}
              item={item}
              disabled={hasReachedLimit}
              onSelect={onSelect}
              sharedByYou
            />
          ))}
        </YStack>
      </YStack>
    );
  }
);

export const RoutineLibraryCompactSection = observer(
  function RoutineLibraryCompactSection({
    frequencies = [],
    query = '',
    onAskLittlebird,
    onBrowseLibrary,
    onSelectTemplate,
  }: RoutineLibraryCompactSectionProps) {
    const { items, isLoading, isError } = useRoutineLibrary();
    const { suggestions: generated, isLoading: isGeneratedLoading } =
      useRoutineSuggestions();
    const { routines } = useStores();
    const { hasReachedLimit } = useRoutinesLimitState();
    const normalizedQuery = query.trim().toLowerCase();
    const existingRoutines = routines.userRoutines;
    const existingTitles = new Set(
      existingRoutines.map((routine) => normalizeRoutineTitle(routine.title))
    );
    const generatedSuggestions = dedupeRoutineSuggestions(
      generated,
      existingRoutines
    );
    const showGenerated =
      frequencies.length === 0 &&
      normalizedQuery.length === 0 &&
      generatedSuggestions.length > 0;
    const matchingItems = items.filter(
      (item) =>
        (frequencies.length === 0 ||
          (item.frequency !== null && frequencies.includes(item.frequency))) &&
        (normalizedQuery.length === 0 ||
          item.title.toLowerCase().includes(normalizedQuery) ||
          item.description.toLowerCase().includes(normalizedQuery))
    );
    const suggestions = FEATURED_SUGGESTION_TITLES.map((title) =>
      matchingItems.find(
        (item) =>
          normalizeRoutineTitle(item.title) === normalizeRoutineTitle(title)
      )
    )
      .filter((item): item is RoutineLibraryItem => item !== undefined)
      .filter((item) => !existingTitles.has(normalizeRoutineTitle(item.title)));

    const shownItems = showGenerated
      ? generatedSuggestions.map(suggestionToRoutineTemplate)
      : suggestions;

    useTemplateImpressions('suggestions', shownItems);

    const onSelect = (item: RoutineLibraryItem) => {
      if (hasReachedLimit) return;
      trackEvent('ROUTINE_LIBRARY_EVENT', {
        actionName: 'library_template_clicked',
        templateId: item.id,
        templateTitle: item.title,
        templateSource: templateSourceOf(item),
        surface: 'suggestions',
      });
      onSelectTemplate(item);
    };

    const onSelectGenerated = (suggestion: GeneratedRoutineSuggestion) => {
      if (hasReachedLimit) return;
      const item = suggestionToRoutineTemplate(suggestion);
      trackEvent('ROUTINE_LIBRARY_EVENT', {
        actionName: 'generated_suggestion_clicked',
        suggestionId: suggestion.id,
        suggestionTitle: suggestion.title,
        templateId: item.id,
        templateSource: 'generated',
        surface: 'suggestions',
      });
      onSelectTemplate(item);
    };

    const onDismissGenerated = (suggestion: GeneratedRoutineSuggestion) => {
      trackEvent('ROUTINE_LIBRARY_EVENT', {
        actionName: 'generated_suggestion_dismissed',
        suggestionId: suggestion.id,
      });
      void routines
        .dismissRoutineSuggestion(suggestion.id)
        .catch(() => undefined);
    };

    const browseLibrary = () => {
      trackEvent('ROUTINE_LIBRARY_EVENT', {
        actionName: 'library_browse_clicked',
      });
      onBrowseLibrary();
    };

    const askLittlebird = () => {
      trackEvent('ROUTINE_LIBRARY_EVENT', {
        actionName: 'library_ask_littlebird_clicked',
      });
      onAskLittlebird();
    };

    if (isLoading || isGeneratedLoading) {
      return (
        <YStack className="gap-2">
          <Skeleton className="h-4 w-40" />
          {[...Array(3)].map((_, index) => (
            <Skeleton key={index} className="h-16 rounded-2xl" />
          ))}
        </YStack>
      );
    }

    if (!showGenerated && (isError || suggestions.length === 0)) return null;

    return (
      <YStack className="gap-2">
        <XStack className="items-center justify-between gap-3 px-2">
          <Text className="text-sm text-foreground-muted">
            {showGenerated ? 'Suggested for you' : 'Suggestions'}
          </Text>
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 text-foreground-muted hover:text-foreground"
            onClick={askLittlebird}
          >
            <Icons.sparkles className="size-3.5" />
            Ask Littlebird
          </Button>
        </XStack>
        <YStack className="gap-1">
          {showGenerated
            ? generatedSuggestions.map((suggestion) => (
                <div key={suggestion.id} className="group relative">
                  <CompactLibraryRow
                    item={suggestionToRoutineTemplate(suggestion)}
                    disabled={hasReachedLimit}
                    onSelect={() => onSelectGenerated(suggestion)}
                  />
                  <button
                    type="button"
                    title="Dismiss suggestion"
                    aria-label="Dismiss suggestion"
                    onClick={() => onDismissGenerated(suggestion)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-1.5 text-foreground-subtle opacity-0 touch:opacity-100 transition-opacity hover:text-foreground group-hover:opacity-100 focus-visible:opacity-100 group-has-[:focus-visible]:opacity-100"
                  >
                    <Icons.cancel className="size-4" />
                  </button>
                </div>
              ))
            : suggestions.map((item) => (
                <CompactLibraryRow
                  key={item.id}
                  item={item}
                  disabled={hasReachedLimit}
                  onSelect={onSelect}
                />
              ))}
          <BrowseLibraryRow onSelect={browseLibrary} />
        </YStack>
      </YStack>
    );
  }
);
